<template>
  <AppModal v-bind="$attrs" v-model="value" max-width="500px" persistent :disable-close="loading">
    <template v-slot:title>
      Merge Existing Vegetative Plot Into Alias
    </template>
    <template v-slot:text>
      <p>Merge a vegetative plot into this location. The selected plot's aliases, specimen, and surveys will be moved
        to this location.</p>
      <p><strong>Note:</strong> Vegetative plot that contain non-primary aliases cannot be merged. Please separate
        the non-primary aliases then merge each alias into this plot.</p>

      <v-form ref="form" v-model="formIsValid">
        <v-autocomplete v-model="plotToBeMergedFormField.value" :rules="[...plotToBeMergedFormField.rules, _samePlotTypeRule, _sameCommunityTypeRule]" required label="Vegetative Plot to merge" :items="plotsEligibleForMerge" item-text="PrimaryName" item-value="Id" return-object :disabled="loading" />
        <v-spacer />
        <p class="secondary--text font-italic">Why can't I select a certain plot? <v-btn icon small @click="showPlotFilterInfo = !showPlotFilterInfo">
            <v-icon>info</v-icon>
          </v-btn>
        </p>
        <v-expand-transition>
          <p v-show="showPlotFilterInfo">
            Not every plot is compatible for merging. Eligible plots must have only one synonym, not have
            any nested plots, have the same plot type as the current plot, and have the same community type as the
            current plot.
          </p>
        </v-expand-transition>
        <v-checkbox color="error" v-model="assignOptions.isTestRun" label="Test Run" persistent-hint hint="See logs of the outcome without making any changes" :disabled="loading" />
        <template v-if="isSystemAdmin">
          <v-subheader class="pl-0 mt-2">System Administrator Settings</v-subheader>
          <v-checkbox class="mt-0" color="info" v-model="assignOptions.makePrimary" label="Make primary name" persistent-hint hint="New synonym will become the primary name" :disabled="loading" />
        </template>
      </v-form>
      <v-expand-transition>
        <v-alert v-show="assignOptions.makePrimary" key="alert" outline class="my-0" type="warning" :value="assignOptions.makePrimary">
          <div class="primary--text">
            The primary alias name shows up in reports, which may cause confusion when the primary alias does not match
            the location name.
          </div>
        </v-alert>
      </v-expand-transition>
      <ExecutionResultsDialog title="Move Data Source" @close="close()" :shouldClose="!assignOptions.isTestRun" ref="executionResultsDialog" />
    </template>
    <template v-slot:actions>
      <v-spacer />
      <v-btn color="success" :loading="loading" :disabled="!formIsValid" @click="submit">
        <v-icon class="mr-1">done</v-icon> {{$t('Button.Save')}}
      </v-btn>
      <v-btn outline color="secondary" @click="close" :disalbed="loading">{{$t('Button.Close')}}</v-btn>
    </template>
  </AppModal>
</template>
<script>
  import {
    createNamespacedHelpers,
  } from 'vuex'
  const authenticate = createNamespacedHelpers('authenticate')
  const datasource = createNamespacedHelpers('datasource')
  const monitoringfeature = createNamespacedHelpers('monitoringfeature')
  const navigation = createNamespacedHelpers('navigation')

  import {
    SOURCE_TYPES,
  } from '@/store/MonitoringFeature/monitoringfeature'

  import {
    stringSorter,
  } from '@/helpers/sorting'
  export default {
    props: {
      dataSourceIndex: {
        type: Object,
        require: true,
      },
    },
    data() {
      return {
        value: false,
        formIsValid: true,
        plotToBeMergedFormField: {
          value: {},
          rules: [
            v => (!!v && Object.keys(v).length > 0) || 'Required',
            v => (!(v && v.Synonyms) || v.Synonyms.length <= 1) ||
            'Plot contains more than one synonym, please separate the non-primary aliases then merge each alias into this plot.',
          ],
        },
        assignOptions: {
          isTestRun: true,
          canCompromise: false,
          shouldMovePrimaryAlias: true,
          makePrimary: false,
        },
        loading: false,
        showPlotFilterInfo: false,
      }
    },
    computed: {
      ...authenticate.mapGetters(['isSystemAdmin']),
      ...datasource.mapGetters(['getDataSourcesGivenMF']),
      ...monitoringfeature.mapGetters(['currentMonitoringFeature']),
      vegetativeDataSources() {
        return this.getDataSourcesGivenMF(SOURCE_TYPES.vegetativeMonitoringEnvirodata)
      },
      plotsEligibleForMerge() {
        if (!this.vegetativeDataSources) {
          return []
        }
        const notCurrentPlotFilter = x => x.Id != this.dataSourceIndex.Id
        const haveMatchingPlotTypeFilter = x => x.PlotType == this.dataSourceIndex.PlotType
        const haveMatchingCommunityTypeFilter = x => x.CommunityType == this.dataSourceIndex.CommunityType
        const noNestedPlotFilter = x => !x.ChildrenPlots || x.ChildrenPlots.length == 0
        let plots = this.vegetativeDataSources.filter(x =>
          notCurrentPlotFilter(x) &&
          haveMatchingPlotTypeFilter(x) &&
          haveMatchingCommunityTypeFilter(x) &&
          noNestedPlotFilter(x)
        )

        if (plots) {
          plots.sort((a, b) => stringSorter(a.PrimaryName, b.PrimaryName))
        }
        return plots
      },
    },
    watch: {
      value() {
        if (!this.value) {
          this.reset()
        }
      },
    },
    methods: {
      ...datasource.mapActions(['setDSMonitoringFeatureId', 'fetchDataSources']),
      ...monitoringfeature.mapActions(['refreshCurrentMFDetails', 'fetchMonitoringFeaturesUI']),
      ...navigation.mapActions(['fetchSideMenu']),
      show() {
        this.value = true
      },
      reset() {
        this.plotToBeMergedFormField.value = {}
        this.$refs.form.resetValidation()
      },
      close() {
        this.value = false
        this.reset()
      },
      async submit() {
        if (!this.$refs.form.validate()) return
        this.loading = true
        try {
          const payload = {
            sourceType: SOURCE_TYPES.vegetativeMonitoringEnvirodata,
            sourceId: this.plotToBeMergedFormField.value.Id,
            monitoringFeatureId: this.currentMonitoringFeature.Id,
            canCompromise: this.assignOptions.canCompromise,
            shouldMovePrimaryAlias: this.assignOptions.shouldMovePrimaryAlias,
            isTestRun: this.assignOptions.isTestRun,
            makePrimary: this.assignOptions.makePrimary,
            baseSynonym: {
              synonymData: {},
            },
          }
          let executionResults = await this.setDSMonitoringFeatureId(payload)
          if (!this.assignOptions.isTestRun) {
            await this.refresh()
          }
          this.$refs.executionResultsDialog.show(executionResults)
        } catch (err) {
          console.error(err)
          this.$refs.executionResultsDialog.show(err)
        } finally {
          this.loading = false
        }
      },
      async refresh() {
        const fetchIndicesPromise = this.fetchDataSources({
          sourceTypes: [SOURCE_TYPES.vegetativeMonitoringEnvirodata],
        })
        const fetchDetailsPromise = this.refreshCurrentMFDetails()
        const fetchAllMFsPromise = this.fetchMonitoringFeaturesUI()
        const fetchSideMenuPromise = this.fetchSideMenu()
        await Promise.all([fetchIndicesPromise, fetchDetailsPromise, fetchAllMFsPromise, fetchSideMenuPromise])
      },
      _samePlotTypeRule(v) {
        return (v && v.PlotType) == (this.dataSourceIndex && this.dataSourceIndex.PlotType) ||
          `Selected plot's type ${(v && v.PlotType) || 'null'} must match current plot type ${(this.dataSourceIndex && this.dataSourceIndex.PlotType) || ''}`
      },
      _sameCommunityTypeRule(v) {
        return (v && v.CommunityType) == (this.dataSourceIndex && this.dataSourceIndex.CommunityType) ||
          `Selected plot's community type ${(v && v.CommunityType) || 'null'} must match current community type ${(this.dataSource && this.dataSource.CommunityType) || ''}`
      },
    },
  }
</script>